import * as THREE from "three";

// Distance kept equal to the default camera-to-origin distance
// (|[20,20,20]| ≈ 34.64) so depth-based fog on the model stays constant
// while focusing; framing comes from ortho zoom only.
export const FOCUS_DIST = 64.64;
// Near-horizon front view. Elevation matches the OrbitControls bottom limit
// (maxPolarAngle = PI/2 - PI/36, i.e. 5° above the horizon); the y component
// is tan(5°) scaled by the horizontal length of [1,0,1]. Same 45° azimuth as
// the free-roam camera, so models rotated to face it still do.
export const FOCUS_DIR = [1, Math.SQRT2 * Math.tan(Math.PI / 36), 1];

// Focus info side panel (ProjectFocusCard) width on desktop: a quarter of
// the viewport of content, floored for readability, plus 9rem of padding
// and feathered edge (pl-10 + pr-[6.5rem] in the card's className — keep
// in sync). CameraRig shifts the focus framing by half of this so the
// model centers in the area right of the panel.
export const FOCUS_PANEL_FRACTION = 0.25;
export const FOCUS_PANEL_MIN_PX = 320;
// 9rem at the default 16px root font size.
export const FOCUS_PANEL_CHROME_PX = 144;

export function focusPanelWidth(viewportWidth) {
  return (
    Math.max(viewportWidth * FOCUS_PANEL_FRACTION, FOCUS_PANEL_MIN_PX) +
    FOCUS_PANEL_CHROME_PX
  );
}

// Camera look-at point shared between CameraRig (which animates it) and
// OrbitControls (which is seeded from it on every remount, so a fresh
// controls instance never snaps the view back to the origin).
export const FOCUS_RIG_TARGET = new THREE.Vector3();

const box = new THREE.Box3();
const sphere = new THREE.Sphere();

// World-space bounding center + the ortho zoom that fits the object on
// screen with margin for the focus card. Computed at click time so it works
// for any model regardless of scale or animation pose.
export function computeFocusFrame(object, size) {
  box.setFromObject(object);
  box.getBoundingSphere(sphere);
  const radius = Math.max(sphere.radius, 0.5);
  // Desktop: leave room for the side panel (width * 0.5). Mobile: the panel
  // is a bottom sheet instead, so the model fits the visible area above it
  // (~54vh) and most of the width.
  const fitPx =
    size.width >= 768
      ? Math.min(size.height * 0.7, size.width * 0.5)
      : Math.min(size.height * 0.38, size.width * 0.8);
  const zoom = THREE.MathUtils.clamp(fitPx / (2 * radius), 12, 200);
  return { center: sphere.center.toArray(), zoom, radius };
}
